import { lockScroll, unlockScroll } from '@theme/utilities';

class NarcissaVideoReels extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.cards = [...this.querySelectorAll('[data-card]')];
    this.dialog = this.querySelector('dialog');
    this.content = this.querySelector('[data-dialog-content]');
    this.mobile = window.matchMedia('(max-width: 749px)');
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.ratios = new Map();
    this.pageHidden = false;
    this.autoplayPaused = this.reducedMotion.matches;
    this.updateAutoplayButton();

    this.addEventListener('click', (event) => {
      const target = event.target.closest('button');
      if (!target) return;
      if (target.matches('[data-open]')) this.open(this.cards.indexOf(target.closest('[data-card]')), target);
      if (target.matches('[data-close]')) this.closeDialog();
      if (target.matches('[data-previous]')) this.goToPanel(this.activeIndex - 1);
      if (target.matches('[data-next]')) this.goToPanel(this.activeIndex + 1);
      if (target.matches('[data-play-popup]')) this.playPopup();
      if (target.matches('[data-autoplay-toggle]')) {
        this.autoplayPaused = !this.autoplayPaused;
        this.updateAutoplayButton();
        this.syncPreviews();
      }
    }, { signal });
    this.addEventListener('change', (event) => {
      if (!event.target.matches('[data-variant]')) return;
      const option = event.target.selectedOptions[0];
      const panel = event.target.closest('[data-popup-panel]');
      const button = panel.querySelector('[data-buy]');
      panel.querySelector('[data-price]').textContent = option.dataset.price;
      button.disabled = option.dataset.available !== 'true';
      button.textContent = button.disabled ? 'Agotado' : button.dataset.label;
    }, { signal });
    this.dialog.addEventListener('close', () => this.cleanupDialog(), { signal });
    this.dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.closeDialog();
    }, { signal });
    // Reconcile native/external closes without waiting for the queued close event.
    this.dialogObserver = new MutationObserver(() => {
      if (!this.dialog.open) this.cleanupDialog();
    });
    this.dialogObserver.observe(this.dialog, { attributes: true, attributeFilter: ['open'] });
    this.dialog.addEventListener('click', (event) => {
      if (event.target !== this.dialog) return;
      const bounds = this.dialog.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) this.closeDialog();
    }, { signal });
    this.dialog.addEventListener('keydown', (event) => {
      // Keep native keyboard controls on video and selects.
      if (event.target.matches('video, select, input')) return;
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault();
        this.goToPanel(this.activeIndex + (event.key === 'ArrowRight' ? 1 : -1));
      }
    }, { signal });
    this.content.addEventListener('scroll', () => this.onPopupScroll(), { passive: true, signal });
    this.content.addEventListener('scrollend', () => this.settlePopupScroll(), { signal });
    this.resizeObserver = new ResizeObserver(() => {
      const width = this.content.clientWidth;
      if (!this.dialog.open || !width || width === this.popupWidth) return;
      this.popupWidth = width;
      this.content.scrollTo({ left: this.activeIndex * width, behavior: 'instant' });
    });
    this.resizeObserver.observe(this.content);
    this.mobile.addEventListener('change', () => {
      if (!this.mobile.matches && this.dialog.open) this.closeDialog();
      this.syncPreviews();
    }, { signal });
    this.reducedMotion.addEventListener('change', () => {
      this.autoplayPaused = this.reducedMotion.matches;
      this.updateAutoplayButton();
      this.syncPreviews();
    }, { signal });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.player?.pause();
      this.syncPreviews();
    }, { signal });
    window.addEventListener('pagehide', () => {
      this.pageHidden = true;
      this.closeDialog();
      this.pauseAll();
    }, { signal });
    window.addEventListener('pageshow', () => {
      this.pageHidden = false;
      if (!this.dialog.open) this.cleanupDialog();
      this.syncPreviews();
    }, { signal });

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => this.ratios.set(entry.target, entry.intersectionRatio));
      this.syncPreviews();
    }, { threshold: [0, 0.25, 0.55, 0.75, 1] });
    this.cards.forEach((card) => this.observer.observe(card));
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.resizeObserver?.disconnect();
    this.dialogObserver?.disconnect();
    clearTimeout(this.scrollTimer);
    this.closeDialog();
    this.pauseAll();
    this.controller?.abort();
    this.controller = null;
  }

  updateAutoplayButton() {
    const button = this.querySelector('[data-autoplay-toggle]');
    button.textContent = this.autoplayPaused ? 'Reproducir videos' : 'Pausar videos';
    button.setAttribute('aria-pressed', String(this.autoplayPaused));
  }

  loadVideo(video) {
    if (!video || video.hasAttribute('src')) return;
    video.src = video.dataset.src;
    video.load();
  }

  pauseAll() {
    this.querySelectorAll('video').forEach((video) => video.pause());
  }

  syncPreviews() {
    const canPlay = this.mobile.matches && !this.pageHidden && !document.hidden && !this.dialog.open && !this.autoplayPaused;
    let active;
    if (canPlay) {
      active = this.cards.filter((card) => card.dataset.autoplay === 'true' && (this.ratios.get(card) || 0) >= 0.55)
        .sort((a, b) => this.ratios.get(b) - this.ratios.get(a))[0];
    }
    this.cards.forEach((card) => {
      const video = card.querySelector('video');
      if (!video) return;
      if (card === active) {
        this.loadVideo(video);
        video.muted = true;
        video.play().catch(() => {});
      } else video.pause();
    });
  }

  open(index, trigger) {
    if (this.dialog.open || !this.mobile.matches || !this.cards[index]) return;
    this.trigger = trigger;
    this.pauseAll();
    this.panels = this.cards.map((card) => {
      const panel = card.querySelector('template').content.firstElementChild.cloneNode(true);
      panel.inert = true;
      panel.setAttribute('aria-hidden', 'true');
      return panel;
    });
    this.content.replaceChildren(...this.panels);
    this.activeIndex = index;
    this.dialog.showModal();
    lockScroll(this.dialog);
    this.dialogActive = true;
    this.popupWidth = this.content.clientWidth;
    this.content.scrollTo({ left: index * this.popupWidth, behavior: 'instant' });
    this.activatePanel(index);
    this.querySelector('[data-close]').focus({ preventScroll: true });
  }

  goToPanel(index) {
    if (!this.dialog.open || !this.panels[index]) return;
    this.content.scrollTo({
      left: index * this.content.clientWidth,
      behavior: this.reducedMotion.matches ? 'instant' : 'smooth',
    });
  }

  onPopupScroll() {
    if (!this.dialog.open) return;
    if (!this.scrolling && Math.abs(this.content.scrollLeft - this.activeIndex * this.content.clientWidth) < 1) return;
    if (!this.scrolling) {
      this.resumeAfterScroll = this.player ? !this.player.paused : false;
      this.scrolling = true;
      this.player?.pause();
    }
    // Older Safari versions do not emit scrollend. Never settle mid-swipe:
    // the fallback also requires the rail to be aligned with a snap point.
    if (!('onscrollend' in this.content)) {
      clearTimeout(this.scrollTimer);
      this.scrollTimer = setTimeout(() => this.settlePopupScroll(), 160);
    }
  }

  settlePopupScroll() {
    if (!this.dialog.open || !this.content.clientWidth) return;
    const width = this.content.clientWidth;
    const index = Math.max(0, Math.min(this.cards.length - 1, Math.round(this.content.scrollLeft / width)));
    if (Math.abs(this.content.scrollLeft - index * width) > 1) return;
    clearTimeout(this.scrollTimer);
    if (index !== this.activeIndex) this.activatePanel(index);
    else if (this.scrolling && this.resumeAfterScroll) this.playPopup();
    this.scrolling = false;
    this.resumeAfterScroll = false;
  }

  activatePanel(index) {
    const card = this.cards[index];
    const panel = this.panels[index];
    if (!card || !panel) return;
    const focused = document.activeElement;
    this.player?.pause();
    // Keep the media element unlocked by the opening tap. Safari grants audio
    // playback per element, so change its source instead of creating a new player.
    if (this.player && this.playerPlaceholder && this.player.isConnected) {
      this.player.replaceWith(this.playerPlaceholder);
    }
    this.activeIndex = index;
    this.panels.forEach((item) => {
      item.inert = item !== panel;
      item.setAttribute('aria-hidden', String(item !== panel));
    });
    this.dialog.setAttribute('aria-labelledby', panel.querySelector('h3').id);
    this.querySelector('[data-counter]').textContent = `${index + 1} / ${this.cards.length}`;
    this.querySelector('[data-previous]').disabled = index === 0;
    this.querySelector('[data-next]').disabled = index === this.cards.length - 1;
    if (this.dialog.open && (focused?.disabled || focused?.closest('[inert]') || !this.dialog.contains(document.activeElement))) {
      this.querySelector('[data-close]').focus({ preventScroll: true });
    }
    const placeholder = panel.querySelector('[data-popup-video]');
    if (!placeholder) return;
    if (!this.player) {
      this.player = placeholder.cloneNode(true);
      this.player.muted = card.dataset.sound !== 'true';
      this.player.addEventListener('error', () => {
        const message = this.panels[this.activeIndex]?.querySelector('[data-video-error]');
        if (message) message.hidden = false;
      }, { signal: this.controller.signal });
      this.player.addEventListener('playing', () => {
        const button = this.panels[this.activeIndex]?.querySelector('[data-play-popup]');
        if (button) button.hidden = true;
      }, { signal: this.controller.signal });
    }
    this.playerPlaceholder = placeholder;
    placeholder.replaceWith(this.player);
    this.player.poster = placeholder.getAttribute('poster') || '';
    this.player.setAttribute('aria-label', placeholder.getAttribute('aria-label') || 'Video');
    this.player.src = placeholder.dataset.src;
    this.player.load();
    panel.querySelector('[data-video-error]').hidden = true;
    this.playPopup();
  }

  playPopup() {
    if (!this.player?.isConnected || !this.dialog.open || document.hidden) return;
    const player = this.player;
    const panel = this.panels[this.activeIndex];
    player.play().catch((error) => {
      // Respect a browser's playback policy and leave a clear one-tap fallback.
      if (error.name === 'NotAllowedError' && this.dialog.open && this.player === player && panel === this.panels[this.activeIndex]) {
        panel.querySelector('[data-play-popup]').hidden = false;
      }
    });
  }

  closeDialog() {
    if (!this.dialog) return;
    if (this.dialog.open) this.dialog.close();
    // Release scrolling in this same interaction, including before pagehide freezes the page.
    this.cleanupDialog();
  }

  cleanupDialog() {
    // A close event from an earlier opening can arrive after the next showModal().
    if (this.dialog.open) return;
    unlockScroll(this.dialog);
    if (!this.dialogActive) return;
    this.dialogActive = false;
    clearTimeout(this.scrollTimer);
    this.scrolling = false;
    this.resumeAfterScroll = false;
    this.player?.pause();
    this.player = null;
    this.playerPlaceholder = null;
    this.panels = [];
    this.content.replaceChildren();
    this.dialog.removeAttribute('aria-labelledby');
    const trigger = this.trigger;
    this.trigger = null;
    if (this.isConnected && !this.pageHidden) {
      if (this.mobile.matches && trigger?.isConnected) trigger.focus({ preventScroll: true });
      this.syncPreviews();
    }
  }
}

if (!customElements.get('narcissa-video-reels')) customElements.define('narcissa-video-reels', NarcissaVideoReels);
